"use client";

import { useLayoutEffect, useRef } from "react";
import { LiquidSurface } from "../liquid-surface";
import type { CircleActionVariant } from "../circle-action-variants";
import { cn } from "@/lib/utils";

const ORBIT_BUBBLES: ReadonlyArray<{ variant: CircleActionVariant }> = [
  { variant: "sky" },
  { variant: "cyan" },
  { variant: "indigo" },
  { variant: "violet" },
  { variant: "sky" },
  { variant: "cyan" },
];

const ORBIT_CONFIG = {
  sm: {
    stagePx: 82,
    radiusXPx: 31,
    radiusYPx: 19,
    bubblePx: 14,
    spinMs: 2600,
    breatheMs: 1200,
  },
  md: {
    stagePx: 102,
    radiusXPx: 39,
    radiusYPx: 24,
    bubblePx: 17,
    spinMs: 3000,
    breatheMs: 1300,
  },
  lg: {
    stagePx: 138,
    radiusXPx: 52,
    radiusYPx: 32,
    bubblePx: 26,
    spinMs: 3200,
    breatheMs: 1450,
  },
} as const;

type OrbitSize = keyof typeof ORBIT_CONFIG;

function breatheScale(phase: number, min: number, max: number) {
  return min + (max - min) * (0.5 - 0.5 * Math.cos(phase * Math.PI * 2));
}

function OrbitLoaderVisual({
  size,
  label,
}: {
  size: OrbitSize;
  label: string;
}) {
  const config = ORBIT_CONFIG[size];
  const ringRef = useRef<HTMLDivElement>(null);
  const bubbleRefs = useRef<Array<HTMLDivElement | null>>([]);

  useLayoutEffect(() => {
    const ring = ringRef.current;
    if (!ring) return;

    let frameId = 0;
    const start = performance.now();

    const tick = (now: number) => {
      const elapsed = now - start;
      const orbitProgress = (elapsed % config.spinMs) / config.spinMs;
      const radialPhase = (elapsed % config.breatheMs) / config.breatheMs;
      const radialScale = breatheScale(radialPhase, 0.62, 1.18);

      bubbleRefs.current.forEach((bubble, index) => {
        if (!bubble) return;

        const angle =
          orbitProgress * Math.PI * 2 +
          (index / ORBIT_BUBBLES.length) * Math.PI * 2;
        const depth = (Math.sin(angle) + 1) / 2;
        const offset =
          (config.breatheMs / ORBIT_BUBBLES.length) * index;
        const phase = ((elapsed + offset) % config.breatheMs) / config.breatheMs;
        const x = Math.cos(angle) * config.radiusXPx * radialScale;
        const y = Math.sin(angle) * config.radiusYPx * radialScale;
        const breathe = breatheScale(phase, 0.84, 1.16);
        const depthScale = 0.72 + depth * 0.48;
        const scale = breathe * depthScale;

        bubble.style.opacity = String(0.5 + depth * 0.5);
        bubble.style.zIndex = String(Math.round(depth * 20) + 2);
        bubble.style.filter = `saturate(${(0.9 + depth * 0.35).toFixed(2)})`;
        bubble.style.transform =
          `translate(-50%, -50%) translate3d(${x.toFixed(2)}px, ${y.toFixed(2)}px, 0) ` +
          `scale(${scale.toFixed(3)})`;
      });

      frameId = requestAnimationFrame(tick);
    };

    frameId = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frameId);
  }, [config.breatheMs, config.spinMs]);

  return (
    <div
      className="mx-auto flex items-center justify-center overflow-visible"
      role="status"
      aria-live="polite"
      aria-label={label}
      style={{
        width: config.stagePx,
        height: config.stagePx,
      }}
    >
      <div
        ref={ringRef}
        className="relative h-full w-full overflow-visible"
      >
        {ORBIT_BUBBLES.map((bubble, index) => (
          <div
            key={index}
            ref={(node) => {
              bubbleRefs.current[index] = node;
            }}
            className="pointer-events-none absolute left-1/2 top-1/2"
            style={{
              width: config.bubblePx,
              height: config.bubblePx,
              transform: "translate(-50%, -50%)",
              transformOrigin: "50% 50%",
              willChange: "transform, opacity",
            }}
          >
            <LiquidSurface
              variant={bubble.variant}
              tone="tinted"
              shape="circle"
              size="free"
              reactive
              shadow="neutral"
              cursorGlow={false}
              className="h-full w-full"
              aria-hidden
            />
          </div>
        ))}
      </div>
    </div>
  );
}

export interface PageLoadingStateV2Props {
  title?: string;
  description?: string;
  minHeightClassName?: string;
  /**
   * Use inside Modal v2 / side panels: fills the body, modal typography (§5.5),
   * orbit loader, and no skeleton bars.
   */
  embedded?: boolean;
  /** Render only the animated bubbles, without title, description or skeleton. */
  visualOnly?: boolean;
  /** Override the orbit dimensions independently of the surrounding layout. */
  loaderSize?: "sm" | "md" | "lg";
}

export function PageLoadingStateV2({
  title,
  description,
  minHeightClassName = "min-h-[260px]",
  embedded = false,
  visualOnly = false,
  loaderSize,
}: PageLoadingStateV2Props) {
  const size: OrbitSize = loaderSize ?? (embedded ? "sm" : "md");
  const ariaLabel = description
    ? `${title ?? "Cargando"}. ${description}`
    : title ?? "Cargando";
  const useEmbeddedLayout = embedded && !visualOnly;

  return (
    <div
      className={cn(
        "flex w-full items-center justify-center",
        useEmbeddedLayout ? "min-h-[220px] flex-1" : visualOnly ? "w-auto" : minHeightClassName,
        useEmbeddedLayout ? "py-6" : visualOnly ? "" : "py-10"
      )}
    >
      <div className="text-center">
        <div className={cn(!visualOnly && "mb-4")}>
          <OrbitLoaderVisual size={size} label={ariaLabel} />
        </div>

        {!visualOnly ? (
          <>
            <h3
              className={cn(
                "font-semibold text-slate-950 dark:text-white",
                embedded
                  ? "mb-1 text-[13px] leading-snug"
                  : "mb-2 text-base"
              )}
            >
              {title}
            </h3>
            {description ? (
              <p
                className={cn(
                  embedded
                    ? "text-[9px] leading-snug text-slate-500 dark:text-zinc-400"
                    : "text-sm text-slate-500 dark:text-zinc-400"
                )}
              >
                {description}
              </p>
            ) : null}
          </>
        ) : null}
        {!embedded && !visualOnly ? (
          <div className="mx-auto mt-6 max-w-md space-y-2 px-4" aria-hidden>
            <div className="h-3 animate-pulse rounded-full bg-slate-100 dark:bg-zinc-800" />
            <div className="h-3 w-4/5 animate-pulse rounded-full bg-slate-100 dark:bg-zinc-800" />
            <div className="h-3 w-3/5 animate-pulse rounded-full bg-slate-100 dark:bg-zinc-800" />
          </div>
        ) : null}
      </div>
    </div>
  );
}
